
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as authService from '../../services/authService';
import { exportToPdf } from '../../services/pdfService';
import { useToast } from '../../hooks/useToast';
import { XIcon } from '../icons/XIcon';
import { PdfIcon } from '../icons/PdfIcon';
import { AuditIcon } from '../icons/AuditIcon';
import type { GeneratedName } from '../../types';

export const FavoritesPanel: React.FC = () => {
  const { user, setUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleRemoveFavorite = async (name: GeneratedName) => {
    if (!user) return;
    try {
      const updatedUser = await authService.toggleFavoriteName(user.uid, name, true); // true to remove
      if (updatedUser) {
        setUser(updatedUser);
        showToast('Name removed from favorites.');
      }
    } catch (error) {
      showToast('Failed to remove favorite.');
    }
  };

  const handleExportFavorites = async () => {
    if (!user || !user.favorites || user.favorites.length === 0) {
      showToast('No saved names to export.');
      return;
    }
    await exportToPdf(user.favorites, "My Saved Brand Names", user.isPro, user.email);
    const updatedUser = await authService.updateUserStat(user.uid, 'pdfsExported');
    if (updatedUser) {
        setUser(updatedUser);
    }
  };

  const handleAuditClick = (brandName: string) => {
    navigate(`/brand-audit/?brand=${encodeURIComponent(brandName)}`);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-[#1F2937]">My Saved Names</h2>
        <button
          onClick={handleExportFavorites}
          disabled={!user?.favorites || user.favorites.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300 disabled:opacity-50"
        >
          <PdfIcon /> Export Favorites
        </button>
      </div>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2">
        {user?.favorites && user.favorites.length > 0 ? (
          user.favorites.map((fav, index) => (
            <div key={index} className="bg-white p-3 rounded-md border border-gray-200 flex justify-between items-center">
              <span className="font-medium text-gray-800">{fav.name}</span>
              <div className="flex items-center gap-3">
                <button
                    onClick={() => handleAuditClick(fav.name)}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded transition-colors"
                    title="Audit this brand name"
                >
                    <AuditIcon className="w-4 h-4" />
                    Audit
                </button>
                <div className="w-px h-4 bg-gray-300"></div>
                <button
                    onClick={() => handleRemoveFavorite(fav)}
                    className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                    aria-label={`Remove ${fav.name} from favorites`}
                >
                    <XIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 py-4">You haven't saved any names yet. Click the star next to a generated name to save it!</p>
        )}
      </div>
    </div>
  );
};
